import React, { FC, useState, useEffect, useRef } from "react";
import { FaCheck, FaXmark } from "react-icons/fa6";
import { Transition } from "react-transition-group";

const CustomToast: FC<CustomToastProps> = ({
  toastIcon,
  title,
  type,
  subtitle,
  actionMessage,
  actionLink,
  action,
  duration = 4000,
  open,
  handleClose,
}) => {
  const [, setExited] = useState(true);
  const nodeRef = useRef(null);

  // Auto-close toast after the specified duration
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(handleClose, duration);
    return () => clearTimeout(timer); // Cleanup on unmount or toast close
  }, [open, duration, handleClose]);

  const handleCloseToast = (reason: string) => {
    if (reason !== "clickaway") handleClose();
  };

  const handleTransitionEnter = () => setExited(false);
  const handleTransitionExited = () => setExited(true);

  const getBackgroundClass = () =>
    type === "error" ? "bg-danger" : "bg-success";

  const getIcon = () =>
    toastIcon ||
    (type === "error" ? (
      <FaXmark className="w-6 h-6 mr-2" />
    ) : (
      <FaCheck className="w-6 h-6 mr-2" />
    ));

  return (
    <Transition
      in={open}
      timeout={{ enter: 300, exit: 300 }}
      appear
      unmountOnExit
      onEnter={handleTransitionEnter}
      onExited={handleTransitionExited}
      nodeRef={nodeRef}
    >
      {(status) => (
        <div
          ref={nodeRef}
          className={`fixed bottom-4 left-4 max-w-sm z-[999] w-full transform transition-transform ${
            status === "entered" ? "translate-x-0" : "translate-x-full"
          } flex items-center justify-between ${getBackgroundClass()} text-white rounded-lg p-4 shadow-lg`}
        >
          {getIcon()}
          <div className="flex-1 max-w-full">
            <p className="font-semibold text-sm">{title}</p>
            {subtitle && <p className="text-xs">{subtitle}</p>}
            {action && actionMessage && (
              <a
                href={actionLink}
                onClick={action}
                className="text-blue-400 text-xs hover:underline"
              >
                {actionMessage}
              </a>
            )}
          </div>
          <button
            onClick={() => handleCloseToast("button-click")}
            className="flex-shrink-0 bg-transparent text-white hover:bg-gray-700 rounded-full p-1"
          >
            <FaXmark />
          </button>
        </div>
      )}
    </Transition>
  );
};

export default CustomToast;

type CustomToastProps = {
  toastIcon?: React.ReactNode;
  title: string;
  type: "error" | "success";
  subtitle?: string;
  actionMessage?: string;
  actionLink?: string;
  action?: () => void;
  duration?: number;
  open: boolean;
  handleClose: () => void;
};
